import React, { useState } from 'react';
import Icon from '../AppIcon';
import Button from '../ui/Button';
import OptimizedImage from '../ui/OptimizedImage';
import { OptimizedAudio, OptimizedVideo } from '../ui/OptimizedMedia';

const TestimonialCard = ({ testimonial, compact = false }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showFullGallery, setShowFullGallery] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showAudioModal, setShowAudioModal] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  const hasBeforeAfter = testimonial?.content?.beforeAfterPhotos?.length > 0;
  const hasWhatsApp = testimonial?.content?.whatsappScreenshots?.length > 0;
  const hasVideo = testimonial?.content?.videos?.length > 0;
  const hasAudio = testimonial?.content?.audioTestimonial;

  const nextImage = () => {
    if (hasBeforeAfter) {
      setCurrentImageIndex((prev) => 
        (prev + 1) % testimonial.content.beforeAfterPhotos.length
      );
    }
  };

  const prevImage = () => {
    if (hasBeforeAfter) {
      setCurrentImageIndex((prev) => 
        prev === 0 ? testimonial.content.beforeAfterPhotos.length - 1 : prev - 1
      );
    }
  };

  const getLeadTemperatureColor = (temperature) => {
    switch (temperature) {
      case 'hot': return 'border-primary bg-primary/5';
      case 'warm': return 'border-warning bg-warning/5';
      case 'cold': return 'border-accent bg-accent/5';
      default: return 'border-border bg-card';
    }
  };

  const getLeadTemperatureIcon = (temperature) => {
    switch (temperature) {
      case 'hot': return 'Target';
      case 'warm': return 'TrendingUp';
      case 'cold': return 'Flame';
      default: return 'User';
    }
  };

  if (compact) {
    return (
      <div className={`rounded-xl border p-4 ${getLeadTemperatureColor(testimonial.leadTemperature)}`}>
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0">
            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
              <Icon name={getLeadTemperatureIcon(testimonial.leadTemperature)} size={20} className="text-primary" />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2 mb-1">
              <h4 className="font-heading text-sm font-semibold text-foreground">
                {testimonial.name}
              </h4>
              <span className="font-caption text-xs text-muted-foreground">
                {testimonial.age} años, {testimonial.location}
              </span>
            </div>
            <div className="flex items-center space-x-3 text-xs text-muted-foreground">
              {hasBeforeAfter && (
                <span className="flex items-center space-x-1">
                  <Icon name="Image" size={12} />
                  <span>{testimonial.content.beforeAfterPhotos.length} fotos</span>
                </span>
              )}
              {hasVideo && (
                <span className="flex items-center space-x-1">
                  <Icon name="Play" size={12} />
                  <span>Video</span>
                </span>
              )}
              {hasAudio && (
                <span className="flex items-center space-x-1">
                  <Icon name="Mic" size={12} />
                  <span>Audio</span>
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-2xl border p-6 ${getLeadTemperatureColor(testimonial.leadTemperature)}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
            <Icon name={getLeadTemperatureIcon(testimonial.leadTemperature)} size={24} className="text-primary" />
          </div>
          <div>
            <h3 className="font-heading text-lg font-semibold text-foreground">
              {testimonial.name}
            </h3>
            <p className="font-body text-sm text-muted-foreground">
              {testimonial.age} años • {testimonial.location}
            </p>
          </div>
        </div>
        <div className="text-right">
          <div className="font-caption text-xs text-muted-foreground">
            Resultados en
          </div>
          <div className="font-body-semibold text-sm text-foreground">
            {testimonial.results?.timeframe}
          </div>
        </div>
      </div>

      {/* Before/After Photos Carousel */}
      {hasBeforeAfter && (
        <div className="mb-4">
          <div className="relative">
            <div className="aspect-[4/3] bg-muted rounded-lg overflow-hidden">
              <img
                src={`${testimonial.content.beforeAfterPhotos[currentImageIndex]}?t=${Date.now()}`}
                alt={`${testimonial.name} transformación ${currentImageIndex + 1}`}
                className="w-full h-full object-cover"
                loading="eager"
                onError={(e) => {
                  // Try fallback to jpg version without cache buster
                  const originalSrc = e.target.src.split('?')[0]; // Remove cache buster
                  const jpgSrc = originalSrc.replace('.webp', '.jpg');
                  if (!e.target.src.includes('.jpg')) {
                    e.target.src = jpgSrc;
                  }
                }}
              />
            </div>
            
            {testimonial.content.beforeAfterPhotos.length > 1 && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute left-2 top-1/2 -translate-y-1/2 w-12 h-12 bg-background/90 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-background transition-smooth"
                  aria-label="Foto anterior"
                >
                  <Icon name="ChevronLeft" size={20} />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-12 h-12 bg-background/90 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-background transition-smooth"
                  aria-label="Siguiente foto"
                >
                  <Icon name="ChevronRight" size={20} />
                </button>
                
                {/* Image counter */}
                <div className="absolute bottom-2 right-2 bg-background/90 backdrop-blur-sm rounded-full px-2 py-1">
                  <span className="font-caption text-xs text-foreground">
                    {currentImageIndex + 1}/{testimonial.content.beforeAfterPhotos.length}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Results */}
      {testimonial.results?.keyAchievements && (
        <div className="mb-4">
          <h4 className="font-body-semibold text-sm text-foreground mb-2">
            Resultados principales:
          </h4>
          <ul className="space-y-1">
            {testimonial.results.keyAchievements.map((achievement, index) => (
              <li key={index} className="flex items-start space-x-2">
                <Icon name="Check" size={14} className="text-success mt-0.5 flex-shrink-0" />
                <span className="font-body text-sm text-muted-foreground">
                  {achievement}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-2">
        {hasWhatsApp && (
          <Button
            variant="outline"
            size="sm"
            iconName="MessageCircle"
            iconPosition="left"
            onClick={() => setShowFullGallery(true)}
          >
            Ver WhatsApp ({testimonial.content.whatsappScreenshots.length})
          </Button>
        )}
        
        {hasVideo && (
          <Button
            variant="outline"
            size="sm"
            iconName="Play"
            iconPosition="left"
            onClick={() => setShowVideoModal(true)}
          >
            Ver Video
          </Button>
        )}
        
        {hasAudio && (
          <Button
            variant="outline"
            size="sm"
            iconName="Mic"
            iconPosition="left"
            onClick={() => setShowAudioModal(true)}
          >
            Escuchar Testimonio
          </Button>
        )}
      </div>

      {/* Tags */}
      {testimonial.tags && testimonial.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-4 pt-4 border-t-2 border-border">
          {testimonial.tags.map((tag) => (
            <span
              key={tag}
              className="px-2 py-1 bg-muted rounded-full text-xs font-caption text-muted-foreground"
            >
              #{tag}
            </span>
          ))}
        </div>
      )}

      {/* WhatsApp Gallery Modal */}
      {showFullGallery && hasWhatsApp && (
        <div className="fixed inset-0 bg-background/95 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-2xl max-w-4xl max-h-[90vh] w-full overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b border-border">
              <h3 className="font-heading text-lg font-semibold">
                Conversaciones WhatsApp - {testimonial.name}
              </h3>
              <button
                onClick={() => setShowFullGallery(false)}
                className="w-12 h-12 flex items-center justify-center hover:bg-muted rounded-full transition-smooth"
                aria-label="Cerrar galería"
              >
                <Icon name="X" size={20} />
              </button>
            </div>
            <div className="p-4 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {testimonial.content.whatsappScreenshots.map((screenshot, index) => (
                  <img
                    key={index}
                    src={screenshot}
                    alt={`WhatsApp ${testimonial.name} ${index + 1}`}
                    className="w-full rounded-lg"
                    loading="lazy"
                    onError={(e) => {
                      const jpgSrc = e.target.src.replace('.webp', '.jpg');
                      if (e.target.src !== jpgSrc) {
                        e.target.src = jpgSrc;
                      }
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Video Modal */}
      {showVideoModal && hasVideo && (
        <div className="fixed inset-0 bg-background/95 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-2xl max-w-4xl w-full">
            <div className="flex justify-between items-center p-4 border-b border-border">
              <h3 className="font-heading text-lg font-semibold">
                Video testimonio - {testimonial.name}
              </h3>
              <button
                onClick={() => setShowVideoModal(false)}
                className="w-12 h-12 flex items-center justify-center hover:bg-muted rounded-full transition-smooth"
                aria-label="Cerrar video"
              >
                <Icon name="X" size={20} />
              </button>
            </div>
            <div className="p-4">
              <video
                controls
                className="w-full rounded-lg"
                src={testimonial.content.videos[0]}
              >
                Tu navegador no soporta el elemento de video.
              </video>
            </div>
          </div>
        </div>
      )}

      {/* Audio Modal */}
      {showAudioModal && hasAudio && (
        <div className="fixed inset-0 bg-background/95 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-2xl max-w-md w-full">
            <div className="flex justify-between items-center p-4 border-b border-border">
              <h3 className="font-heading text-lg font-semibold">
                Audio testimonio - {testimonial.name}
              </h3>
              <button
                onClick={() => setShowAudioModal(false)}
                className="w-12 h-12 flex items-center justify-center hover:bg-muted rounded-full transition-smooth"
                aria-label="Cerrar audio"
              >
                <Icon name="X" size={20} />
              </button>
            </div>
            <div className="p-6 text-center">
              <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Mic" size={32} className="text-primary" />
              </div>
              <audio
                controls
                className="w-full mb-4"
                src={testimonial.content.audioTestimonial}
              >
                Tu navegador no soporta el elemento de audio.
              </audio>
              <p className="font-body text-sm text-muted-foreground">
                Testimonio de {testimonial.name}, {testimonial.age} años
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestimonialCard;